import { InputAdornment, Popper, useTheme } from "@mui/material";
import {
  Autocomplete,
  Text,
} from "@totalenergiescode/mobility-business-rev-design-system";
import React, { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import { useGlobalSearch } from "@/api/graphql/globalSearch/hooks";
import {
  CompanyDataType,
  EntityType,
  UserDataType,
} from "@/api/graphql/globalSearch/types";
import { useDebounce } from "@/hooks/useDebounce/hook";

import SearchExample from "./details/example/component";
import SearchGroup from "./details/group/component";
import SearchSingleOption from "./details/single/component";
import { useSearchAutocomplete } from "./hooks";
import {
  getTextFieldInputSx,
  StyledAdornmentContainer,
  StyledDiv,
  StyledIconContainer,
  StyledPopOverContainer,
  StyledSearchField,
  StyledSearchIcon,
} from "./styles";
import {
  CustomPaperComponent,
  CustomPaperComponentProps,
  PopperCustomComponent,
  PopperCustomComponentProps,
  SearchResultOption,
} from "./types";

const PopperMy: PopperCustomComponent = ({ searchTerm, ...props }) => (
  <Popper sx={{ width: "100%" }} {...props}>
    {searchTerm && searchTerm.length > 2 ? (
      <div data-testid='entities__entities-search-content'>
        {props.children as ReactNode}
      </div>
    ) : (
      <StyledPopOverContainer>
        <StyledDiv sx={{ padding: "30px" }}>
          <SearchExample />
        </StyledDiv>
      </StyledPopOverContainer>
    )}
  </Popper>
);

const PaperMy: CustomPaperComponent = (props) => {
  const {
    options,
    searchTerm,
    totalUsersResults,
    totalAccountResults,
    ...restProps
  } = props;

  const { t: tSearch } = useTranslation("search");

  return (
    <StyledPopOverContainer {...restProps}>
      <StyledDiv sx={{ padding: "15px" }}>
        {options && options.length > 0 && (
          <Text
            sx={{ marginBottom: "10px", color: "#0077FB" }}
            variant='title3'
            dataTestId='entities__entities-search-results'
          >
            {tSearch("search.result", {
              count: Number(totalAccountResults) + Number(totalUsersResults),
              term: searchTerm,
            })}
          </Text>
        )}

        <div>
          {Array.isArray(props.children)
            ? React.Children.map(props.children, (child) => child)
            : props.children}
        </div>
      </StyledDiv>
    </StyledPopOverContainer>
  );
};

const Search = () => {
  const theme = useTheme();
  const { t: tSearch } = useTranslation("search");

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 400);

  const {
    data: options,
    isLoading,
    totalAccountResults,
    totalUsersResults,
  } = useGlobalSearch(debouncedSearchTerm);

  const { handleHighlightChange, handleKeyDown, setHighlightedOption } =
    useSearchAutocomplete();

  return (
    <Autocomplete
      id='autocomplete'
      options={options}
      loading={isLoading}
      onClose={() => {
        setSearchTerm("");
        setHighlightedOption(null);
      }}
      autoHighlight
      clearOnBlur
      isOptionEqualToValue={(
        option: SearchResultOption,
        value: SearchResultOption,
      ) => option.id === value.id}
      groupBy={(option: SearchResultOption) => option.type}
      popupIcon={null}
      noOptionsText={<SearchExample isNoOptionsVersion />}
      onHighlightChange={handleHighlightChange}
      filterOptions={(option) => option}
      getOptionLabel={(option) =>
        (option as SearchResultOption).type === EntityType.User
          ? `${(option as UserDataType).firstName} ${(option as UserDataType).lastName}`
          : (option as CompanyDataType).name
      }
      PaperComponent={PaperMy}
      PopperComponent={PopperMy}
      renderOption={(params, option, { inputValue }) => (
        <SearchSingleOption
          key={option.id}
          params={params}
          option={option}
          inputValue={inputValue}
        />
      )}
      renderGroup={(params) =>
        options.length > 0 && (
          <SearchGroup
            key={params.key}
            params={params}
            totalUsersResults={totalUsersResults}
            totalAccountResults={totalAccountResults}
          />
        )
      }
      slotProps={{
        popper: { searchTerm } as PopperCustomComponentProps,
        paper: {
          options,
          searchTerm,
          totalUsersResults,
          totalAccountResults,
        } as CustomPaperComponentProps,
      }}
      renderInput={(props) => (
        <StyledSearchField
          {...props}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={tSearch("search.search")}
          data-testid='entities__entities-search-bar'
          InputProps={{
            ...props.InputProps,
            sx: getTextFieldInputSx(theme),
            startAdornment: (
              <InputAdornment position='start'>
                <StyledAdornmentContainer>
                  <StyledIconContainer>
                    <StyledSearchIcon width={18} height={18} />
                  </StyledIconContainer>
                </StyledAdornmentContainer>
              </InputAdornment>
            ),
          }}
        />
      )}
    />
  );
};

export default Search;
